import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { AuthorizationMode } from '@app/common/authorization-mode';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { Roles } from '@app/common/roles';
import { UserMembershipToGroup } from '@app/models/file';
import { LicenseService } from '@app/services/license.service';
import { Environment } from '@environments/interface.environment';
import { EMPTY, forkJoin, Observable, of, concat } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, expand, tap, toArray } from 'rxjs/operators';
import { AuthService } from '@app/services/auth.service';
import { CacheService } from '@app/services/cache.service';

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  getMyGroups$: Observable<Array<UserMembershipToGroup>>;

  constructor(private http: HttpClient, @Inject(CUSTOMERENVIRONMENT) private environment: Environment, private httpHelper: HttpHelper, private licenseService: LicenseService, private authService: AuthService, private cache: CacheService) { }

  private isUserMemberOfSharePointGroup$ = new Map<string, Observable<boolean>>();
  private roles$: Observable<Array<string>>;
  public useCache = true;
  private readonly cacheKeyMyGroups = 'SecurityService-getMyGroups';

  isUserMemberOfSharePointGroup(groupTitle: string): Observable<boolean> {
    // NOTE: The property OnlyAllowMembersViewMembership must be set to true for the group you want to check.
    if (!this.isUserMemberOfSharePointGroup$.has(groupTitle)) {
      const httpRequest = this.licenseService.hasUserAny365License()
        .pipe(
          mergeMap(hasLicense => {
            if (hasLicense) {
              return this.http
                .get<any>(`${this.environment.coreWorkplaceSiteUrl}/_api/web/sitegroups/getbyname('${groupTitle}')/CanCurrentUserViewMembership`)
                .pipe(
                  map(response => {
                    return response.value;
                  }));
            } else {
              return of(false);
            }
          }),
          catchError(this.httpHelper.handleError({ operation: 'isUserMemberOfGroup', result: false })),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      this.isUserMemberOfSharePointGroup$.set(groupTitle, httpRequest);
    }
    return this.isUserMemberOfSharePointGroup$.get(groupTitle);
  }

  checkMembershipToOffice365Groups(groupIds: Array<string>): Observable<Array<UserMembershipToGroup>> {
    if (groupIds.length > 0) {
      return this.http
        .post<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/me/checkMemberGroups`, { 'groupIds': groupIds })
        .pipe(map(response => {
          return groupIds.map(gid => {
            const membershipToGroupIds = response.value as Array<string>;
            return {
              GroupId: gid,
              UserIsMember: membershipToGroupIds && membershipToGroupIds.length > 0 ? membershipToGroupIds.some(a => a.toLowerCase() === gid.toLowerCase()) : false
            };
          });
        }));
    } else {
      return of(new Array<UserMembershipToGroup>());
    }
  }

  checkOffice365GroupsMemberships(): Observable<Array<UserMembershipToGroup>> {
    if (this.getMyGroups$) {
      return this.getMyGroups$;
    } else {
      this.getMyGroups$ =
      this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/me/transitiveMemberOf/microsoft.graph.group`)
      .pipe(
        expand((results) => {
          if (results.hasOwnProperty('@odata.nextLink')) {
            return this.http.get<any>(results['@odata.nextLink']);
          } else {
            return EMPTY;
          }
        }),
        toArray(),
        map(results => {
          let ids = new Array<UserMembershipToGroup>();
          results.forEach( result => {
            result.value.map(group => {
              ids = ids.concat ( {
                GroupId: group.id,
                UserIsMember: true
              } as UserMembershipToGroup);
            });
          });
          return ids;
        }),
        tap(response => {
          this.useCache && this.cache.set(this.cacheKeyMyGroups, response as Array<UserMembershipToGroup>);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<UserMembershipToGroup>>(this.cacheKeyMyGroups);
        if (cachedData) {
          return concat(of(cachedData), this.getMyGroups$);
        }
      }
      return this.getMyGroups$;
    }
  }

  getUserRoleClaimValues(): Observable<string[]> {
    const roles = this.authService.idTokenClaims.get('roles') as Array<string>;
    if (!roles || roles.length === 0) {
      if (!this.roles$) {
        this.roles$ = this.http.get<Array<string>>(`${Constants.API_ENDPOINT}/security/roles`)
          .pipe(
            catchError(this.httpHelper.handleError({ operation: 'roles$', result: [] })),
            shareReplay({ bufferSize: 1, refCount: true })
          );
      }
      return this.roles$;
    } else {
      return of(roles);
    }
  }

  hasUserAppRole(role: Roles): Observable<boolean> {
    const roleName = this.getRoleName(role, this.environment.authorizationMode);
    if (this.environment.authorizationMode === AuthorizationMode.Claims) {
      return this.getUserRoleClaimValues()
        .pipe(
          map(roles => {
            return roles.some(r => r === roleName);
          }));
    } else {
      return this.isUserMemberOfSharePointGroup(roleName);
    }
  }

  hasUserAnyAppRole(roles: Roles[]) {
    const obs$ = roles.map(role => this.hasUserAppRole(role));
    return forkJoin(obs$)
      .pipe(
        map(result => {
          return result.some(r => r === true);
        }));
  }

  getRoleName(role: Roles, authMode: AuthorizationMode) {
    switch (authMode) {
      case AuthorizationMode.Claims: {
        if (role === Roles.Admin) {
          return Constants.WORKPLACE_ADMINISTRATORS_ROLE_NAME;
        }
        if (role === Roles.Editor) {
          return Constants.WORKPLACE_EDITORS_ROLE_NAME;
        }
        if (role === Roles.CollabSiteCreator) {
          return Constants.WORKPLACE_COLLABSITECREATOR_ROLE_NAME;
        }
        if (role === Roles.AppEditor) {
          return Constants.WORKPLACE_APP_EDITOR_ROLE_NAME;
        }
        if (role === Roles.MessageBroadcastEditor) {
          return Constants.WORKPLACE_MESSAGEBROADCAST_EDITOR_ROLE_NAME;
        }
        if (role === Roles.FooterEditor) {
          return Constants.WORKPLACE_FOOTER_EDITOR_ROLE_NAME;
        }
        if (role === Roles.EditorDashboardEditor) {
          return Constants.WORKPLACE_EDITORDASHBOARD_EDITOR_ROLE_NAME;
        }
        if (role === Roles.PowerBIEditor) {
          return Constants.WORKPLACE_POWER_BI_EDITOR_ROLE_NAME;
        }
        break;
      }
      case AuthorizationMode.SharePoint:
      default: {
        // Default will use SharePoint as this was the default mode when Workplace was created.
        if (role === Roles.Admin) {
          return Constants.WORKPLACE_ADMINISTRATORS_GROUP_TITLE;
        }
        if (role === Roles.Editor) {
          return Constants.WORKPLACE_EDITORS_GROUP_TITLE;
        }
        if (role === Roles.CollabSiteCreator) {
          return Constants.COLLAB_SITE_CREATION_GROUP_TITLE;
        }
        if (role === Roles.AppEditor) {
          return Constants.WORKPLACE_EDITORS_GROUP_TITLE;
        }
        if (role === Roles.MessageBroadcastEditor) {
          return Constants.WORKPLACE_EDITORS_GROUP_TITLE;
        }
        if (role === Roles.FooterEditor) {
          return Constants.WORKPLACE_EDITORS_GROUP_TITLE;
        }
        if (role === Roles.EditorDashboardEditor) {
          return Constants.WORKPLACE_EDITORS_GROUP_TITLE;
        }
        if (role === Roles.PowerBIEditor) {
          return Constants.WORKPLACE_ADMINISTRATORS_GROUP_TITLE;
        }
        break;
      }
    }
  }

}
